import { LayoutMode } from '@fluidx/office-fluid-types';

declare const ResizeObserver: any;

export function setupInitialIframeLayout(layoutMode: LayoutMode, iframe: HTMLIFrameElement): void {
  switch (layoutMode) {
    case LayoutMode.fixedByHost:
      iframe.style.height = '100%';
      iframe.style.width = '100%';
      break;

    case LayoutMode.growHeight:
      iframe.style.width = '100%';
      break;

    case LayoutMode.grow:
      // Nothing to do
      break;

    default:
      throw new Error(`Invalid layout mode "${layoutMode}"`);
  }
}

export async function setupElementLayoutInsideIframe(
  element: HTMLElement,
  layoutMode: LayoutMode,
  onNeedingToSendSizeOutsideIframe: (desiredSize: { h: number; w?: number }) => void
): Promise<void> {
  const elementStyle = element.style;

  // All components should internally handle overflow
  document.documentElement.style.overflow = 'hidden';

  if (layoutMode === LayoutMode.fixedByHost) {
    elementStyle.position = 'absolute';
    elementStyle.top = '0';
    elementStyle.bottom = '0';
    elementStyle.left = '0';
    elementStyle.right = '0';
  } else {
    if (layoutMode === LayoutMode.growHeight) {
      elementStyle.maxWidth = '100%';
    }

    await ensureResizeObserver();

    // We have observed occasional issues with the ResizeObserver, that it might
    // not fire under certain circumstances (at minimum, having breakpoints in the wrong places --
    // but we had at least one user report of it happening in a "real-world" scenario too).
    // Thus, create a retry mechanism where if the ResizeObserver doesn't fire after a certain duration,
    // keep trying to query the iframe element until we get a reasonable size.
    const TIME_BETWEEN_FALLBACK_RETRY_ATTEMPTS = 50; /* 50ms seems reasonable
      to ensure it checks soon enough but doesn't flood the system */
    let gotReasonableDimensions = false;
    let forceRecomputeTimeout: NodeJS.Timeout | undefined;
    const resizeObserver = new ResizeObserver(getAndSendSize);
    resizeObserver.observe(element);
    // Just in case, kick off an initial fallback as well, in case the observer for some reason is never fired.
    kickOffFallbackSizeCheck();

    // Helpers:

    function getAndSendSize() {
      // Grab the height/width to send outside the iframe so it can have the
      // size of the component contents.
      // Note we're using 'client' dimensions instead of 'scroll' dimensions
      // because ResizeObserver is inconsistent for firing in regards to size
      // changes for 'scroll' measurements. Using 'client' dimensions means
      // that scroll bars should never be allowed to appear on the element
      // we're using to render the component (It's fine if the component
      // itself has scrollbars, just not the element we're measuring here).
      const size: { h: number; w?: number } =
        layoutMode === LayoutMode.growHeight
          ? { h: element.clientHeight }
          : {
              h: element.clientHeight,
              w: element.clientWidth
            };

      if (size.h > 0 || (size.w || 0) > 0) {
        gotReasonableDimensions = true;
        forceRecomputeTimeout && clearTimeout(forceRecomputeTimeout);
        onNeedingToSendSizeOutsideIframe(size);
      } else if (!gotReasonableDimensions && !forceRecomputeTimeout) {
        // If didn't get reasonable dimensions yet, and if a fallback timeout isn't already running,
        // kick off a timeout to fetch the dimensions later.
        kickOffFallbackSizeCheck();
      }
    }

    function kickOffFallbackSizeCheck() {
      forceRecomputeTimeout = setTimeout(() => {
        forceRecomputeTimeout = undefined;
        getAndSendSize();
      }, TIME_BETWEEN_FALLBACK_RETRY_ATTEMPTS);
    }
  }
}

async function ensureResizeObserver() {
  if (typeof ResizeObserver === 'undefined') {
    const module = await import(/* webpackChunkName: "ResizeObserverPolyfill" */ 'resize-observer-polyfill');
    (<any>window).ResizeObserver = module.default;
  }
}
